import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';

const authHeaders = (extra = {}) => {
  const session = SessionManager.getSession('saas_provider') || SessionManager.getCurrentSession();
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const SaaSProviderDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_products: 0,
    total_purchases: 0,
    active_subscriptions: 0,
    total_revenue: 0
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const providerId = localStorage.getItem('dutlaunch_user_id');
  const providerName = localStorage.getItem('dutlaunch_username');

  useEffect(() => {
    const role = localStorage.getItem('dutlaunch_role');
    if (role !== 'saas_provider') {
      navigate('/');
      return;
    }
    
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load provider stats
      const statsRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/saas-provider/dashboard/stats`, { headers: authHeaders() });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load provider products
      const productsRes = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/saas-provider/products`, { headers: authHeaders() });
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SaaS Provider Dashboard</h1>
          <p className="mt-2 text-gray-600">Welcome back, {providerName}!</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_products}</p>
              </div>
              <Icon name="cloud" className="text-blue-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Purchases</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_purchases}</p>
              </div>
              <Icon name="shopping_cart" className="text-green-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_subscriptions}</p>
              </div>
              <Icon name="trending_up" className="text-purple-500 text-3xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">₹{stats.total_revenue.toLocaleString('en-IN')}</p>
              </div>
              <Icon name="payments" className="text-yellow-500 text-3xl" />
            </div>
          </div>
        </div>

        {/* Products List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">My SaaS Products</h2>
              <Button onClick={() => alert('Product creation coming soon!')}>
                <Icon name="add" className="mr-2" />
                Add New Product
              </Button>
            </div>
          </div>

          <div className="p-6">
            {products.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="cloud" className="text-gray-400 text-6xl mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Yet</h3>
                <p className="text-gray-600 mb-6">Start by posting your first SaaS product via API or dashboard</p>
                <Button onClick={() => alert('Product creation coming soon!')}>
                  <Icon name="add" className="mr-2" />
                  Create Your First Product
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{product.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-sm text-gray-500">
                            <Icon name="category" className="inline mr-1" />
                            {product.category}
                          </span>
                          <span className="text-sm text-gray-500">
                            <Icon name="schedule" className="inline mr-1" />
                            {product.pricing_model}
                          </span>
                          <span className="text-sm font-semibold text-primary">
                            ₹{product.price.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
                          Edit
                        </button>
                        <button className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SaaSProviderDashboard;
